/**
 * Compiler-rewrite PR1 — authoritative live compile-input builder.
 *
 * Every production call to `compileWorksheetExecution()` must route through
 * exactly one of the factories in this file. That guarantees:
 *
 *   1. All live paths provide the same adapter input shape, so the active
 *      backend (TS-legacy today, Rust/WASM at cutover) never sees a
 *      surprise-shaped input coming from one caller and a richer one from
 *      another.
 *   2. The two sides of `assertNoWorksheetQueryExecutionDrift` in
 *      `buildLocalWorksheetExecutionCorePlan` are fed byte-identical
 *      compile inputs so the drift assertion measures real divergence
 *      rather than input skew.
 *   3. `callerTag` is tracked explicitly for every live path, which
 *      replaces the ad-hoc strings sprinkled across the old call sites
 *      and gives switch-time diagnostics a reliable provenance label.
 *
 * This file is intentionally free of business logic. `typedSpec`
 * derivation from a `PreparedWorksheetExecutionCore` is a separate,
 * heavier plan item (plan §Workstream A step 6, authoring-to-typed
 * lowering). For now the builder leaves `typedSpec: null`; the adapter's
 * existing fallback keeps TS-legacy as the live backend. Once the Rust
 * backend goes default-on (plan PR6) the acceptance gate will require
 * every production input to carry a `typedSpec`; the derivation change
 * will land here, inside these factories, and every call site gets it
 * for free.
 */

import type { CompileWorksheetExecutionInput } from './worksheetCompilerAdapter'
import type { WorksheetQueryOptions } from './worksheetQueryBuilder'
import type { PreparedWorksheetExecutionCore } from './worksheetExecutionCore'
import type { SemanticWorksheetPlan, VizqlPlan } from './vizqlPlan'
import type { WorksheetSpecV1 } from './spec'
import type { ActionResult } from './actionRuntime'
import type { TypedParameterType, TypedWorksheetSpec } from './worksheetCompilerTyped'
import {
  tryBuildTypedSpecFromInputs,
} from './buildTypedSpecFromAuthoring'
import {
  resolveWorksheetActionState,
  type ResolvedWorksheetActionState,
} from '../dashboard/resolveWorksheetActionState'
import { toWorksheetRuntimeFilters } from '../dashboard/effectiveFilterLowerings'

export type LiveCompileCallerTag =
  | 'renderVizqlPlanExecution'
  | 'buildLocalWorksheetExecutionCorePlan'
  | 'buildLocalWorksheetExecutionCorePlan.semantic'
  | 'worker.run_query_tab'

/**
 * Base options shared by every live factory. Kept deliberately narrow —
 * anything a caller needs to surface beyond these fields should become a
 * first-class field on the builder rather than travel through ad-hoc
 * property bags.
 */
export type WorksheetCompilerInputOptions = {
  /** When true, forces the adapter to emit a DuckDbExecPlan. */
  emitExecPlan?: boolean
  /**
   * Extra flags that the TS-legacy / Rust backends read from
   * queryOptions directly (the worker mirrors `collectExecPlan`,
   * for example). Merged into the final queryOptions shallowly.
   */
  queryOptionsOverrides?: Partial<WorksheetQueryOptions> & Record<string, unknown>
  /**
   * PR3 — pre-resolved dashboard action state. When a caller has already
   * turned a raw `ActionResult` into a `ResolvedWorksheetActionState`
   * (via `resolveWorksheetActionState`), they pass it here. The builder
   * forwards it to the adapter so Rust lowering can consume one
   * normalized shape instead of re-deriving filter semantics in each
   * call site. Takes precedence over `actionState` below.
   */
  resolvedActionState?: ResolvedWorksheetActionState | null
  /**
   * PR3 — raw dashboard action state, resolved by the builder on the
   * caller's behalf. Ignored when `resolvedActionState` is provided.
   */
  actionState?: ActionResult | null
  /**
   * PR3 — explicit worksheet name hint for the resolver. Defaults to
   * the worksheetSpec's name when omitted.
   */
  actionStateWorksheetName?: string | null
  /** PR3 — measure field hints for the resolver. */
  measureFieldNames?: readonly string[]
}

function resolveActionStateForInput(
  worksheetSpec: WorksheetSpecV1,
  options: WorksheetCompilerInputOptions,
): ResolvedWorksheetActionState | null {
  if (options.resolvedActionState) return options.resolvedActionState
  if (!options.actionState) return null
  const fallbackName =
    (worksheetSpec as any)?.source?.tableau?.worksheet_name ??
    (worksheetSpec as any)?.source?.tableau?.worksheetName ??
    (worksheetSpec as any)?.name ??
    null
  const name =
    options.actionStateWorksheetName === undefined
      ? (fallbackName ?? null)
      : options.actionStateWorksheetName
  return resolveWorksheetActionState(options.actionState, name, {
    measureFieldNames: options.measureFieldNames,
  })
}

const resolveRuntimeFiltersForTypedInput = (
  resolvedActionState: ResolvedWorksheetActionState | null,
  optionsFilters?: unknown,
): readonly unknown[] => {
  const actionFilters = resolvedActionState ? toWorksheetRuntimeFilters(resolvedActionState) : []
  const baseFilters = Array.isArray(optionsFilters) ? optionsFilters : []
  if (!actionFilters.length) return baseFilters
  if (!baseFilters.length) return actionFilters
  return [...baseFilters, ...actionFilters]
}

const maybeLogFilterTypedLowering = (args: {
  callerTag: string
  worksheetName: string | null | undefined
  queryOptions: WorksheetQueryOptions | Record<string, unknown>
  runtimeFilters: readonly unknown[]
  typedSpec: { filters?: readonly unknown[] } | null | undefined
  rejectionReason: string | null | undefined
}): void => {
  if (!(args.queryOptions as any)?.debugFilters) return
  try {
    console.log(`[FG-FILTER-TYPED-LOWERING] ${JSON.stringify({
      callerTag: args.callerTag,
      worksheetName: String(args.worksheetName ?? '').trim() || null,
      runtimeFilterCount: args.runtimeFilters.length,
      typedSpecFilterCount: Array.isArray(args.typedSpec?.filters) ? args.typedSpec.filters.length : 0,
      rejectionReason: args.rejectionReason ?? null,
      filters: args.runtimeFilters.slice(0, 24).map((filterObj: any, idx: number) => ({
        idx,
        field_name: String(filterObj?.field_name ?? filterObj?.field ?? '').trim() || null,
        operator: String(filterObj?.operator ?? '').trim().toUpperCase() || null,
        kind: String(filterObj?.kind ?? '').trim().toLowerCase() || null,
        filter_type: String(filterObj?.filter_type ?? '').trim().toLowerCase() || null,
        value_count: Array.isArray(filterObj?.value) ? filterObj.value.length : (filterObj?.value == null ? 0 : 1),
        value_preview: Array.isArray(filterObj?.value) ? filterObj.value.slice(0, 8) : (filterObj?.value ?? null),
      })),
    })}`)
  } catch {}
}

/**
 * Raw parameter values come off the normalized request as JS primitives
 * (strings/numbers/booleans) since that's the legacy TS compiler's wire
 * shape. Rust expects a serde-tagged `TypedCalcLiteral` object. Coerce
 * each value to the narrowest variant that preserves the value. Strings
 * that parse cleanly as finite numbers become `float`/`integer` to match
 * the declared parameter datatype — Tableau often stores measure
 * parameters as strings like `"500000."` even when the declared type is
 * `real`.
 */
const formatIsoDateUtc = (date: Date): string => {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const coerceTemporalBoundParameter = (
  value: unknown,
  ty: Extract<TypedParameterType, 'date' | 'timestamp'>,
): string | null => {
  const readDate = (raw: unknown): Date | null => {
    if (raw instanceof Date) {
      return Number.isFinite(raw.getTime()) ? raw : null
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      const parsed = new Date(raw)
      return Number.isFinite(parsed.getTime()) ? parsed : null
    }
    if (typeof raw === 'string') {
      const trimmed = raw.trim()
      if (!trimmed) return null
      const unwrapped = trimmed.replace(/^#|#$/g, '')
      if (/^\d{12,}$/.test(unwrapped)) {
        const asMs = Number(unwrapped)
        if (Number.isFinite(asMs)) {
          const parsed = new Date(asMs)
          if (Number.isFinite(parsed.getTime())) return parsed
        }
      }
      const parsed = new Date(unwrapped)
      return Number.isFinite(parsed.getTime()) ? parsed : null
    }
    return null
  }

  const parsed = readDate(value)
  if (!parsed) return null
  return ty === 'date' ? formatIsoDateUtc(parsed) : parsed.toISOString()
}

export function coerceBoundParametersForRust(
  raw: Record<string, unknown>,
  declaredTypes?: Map<string, TypedParameterType>,
): Record<string, any> {
  const out: Record<string, any> = {}
  for (const [name, value] of Object.entries(raw)) {
    const declaredType = declaredTypes?.get(name)
    if (declaredTypes && !declaredType) continue
    if (value === null || value === undefined) {
      out[name] = { kind: 'null' }
      continue
    }
    if (declaredType === 'date' || declaredType === 'timestamp') {
      const rawTemporalValue =
        typeof value === 'object' && value !== null && 'kind' in (value as any)
          ? (value as any).value
          : value
      const coercedTemporal = coerceTemporalBoundParameter(rawTemporalValue, declaredType)
      if (coercedTemporal !== null) {
        out[name] = { kind: 'string', value: coercedTemporal }
        continue
      }
    }
    if (typeof value === 'object' && value !== null && 'kind' in (value as any)) {
      out[name] = value
      continue
    }
    if (typeof value === 'boolean') {
      out[name] = { kind: 'bool', value }
      continue
    }
    if (typeof value === 'number') {
      out[name] = Number.isInteger(value)
        ? { kind: 'integer', value }
        : { kind: 'float', value }
      continue
    }
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed !== '' && /^-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(trimmed)) {
        const asNum = Number(trimmed)
        if (Number.isFinite(asNum)) {
          out[name] =
            Number.isInteger(asNum) && !trimmed.includes('.') && !/[eE]/.test(trimmed)
              ? { kind: 'integer', value: asNum }
              : { kind: 'float', value: asNum }
          continue
        }
      }
      if (/^(true|false)$/i.test(trimmed)) {
        out[name] = { kind: 'bool', value: trimmed.toLowerCase() === 'true' }
        continue
      }
      out[name] = { kind: 'string', value }
      continue
    }
    out[name] = { kind: 'string', value: String(value) }
  }
  return out
}

const normalizeParameterLikeKey = (value: unknown): string => {
  const text = String(value ?? '').trim()
  if (!text) return ''
  const bracketed = Array.from(text.matchAll(/\[([^\]]+)\]/g))
    .map((match) => String(match[1] ?? '').trim())
    .filter(Boolean)
  const raw = bracketed.length >= 2 && bracketed[0]?.toLowerCase() === 'parameters'
    ? bracketed[1]
    : text
  return raw
    .replace(/^"+|"+$/g, '')
    .replace(/^'+|'+$/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

const isTableauParameterTokenLike = (value: unknown): boolean => {
  const text = String(value ?? '').trim()
  return /\[Parameters\]\.\[[^\]]+\]/i.test(text)
}

export function coerceInteractionCubeSlicersForTypedSpec(
  rawSlicers: unknown,
  typedSpec: TypedWorksheetSpec | null | undefined,
): Array<{ field: string; alias: string }> {
  if (!typedSpec || !Array.isArray(rawSlicers)) return []
  const parameterKeys = new Set<string>()
  for (const param of typedSpec.parameters ?? []) {
    const nameKey = normalizeParameterLikeKey(param.name)
    if (nameKey) parameterKeys.add(nameKey)
    const captionKey = normalizeParameterLikeKey(param.caption)
    if (captionKey) parameterKeys.add(captionKey)
  }
  const seenSlicerAliases = new Set<string>()
  return rawSlicers
    .map((field: any) => {
      const rawField = String(field?.fieldName ?? field?.field_name ?? field?.displayName ?? field?.display_name ?? field?.columnName ?? field?.column_name ?? '').trim()
      const alias = String(field?.columnName ?? field?.column_name ?? field?.displayName ?? field?.display_name ?? rawField).trim()
      if (!rawField || !alias) return null
      if (
        isTableauParameterTokenLike(rawField) ||
        isTableauParameterTokenLike(alias) ||
        parameterKeys.has(normalizeParameterLikeKey(rawField)) ||
        parameterKeys.has(normalizeParameterLikeKey(alias))
      ) {
        return null
      }
      const key = alias.toLowerCase()
      if (seenSlicerAliases.has(key)) return null
      seenSlicerAliases.add(key)
      return { field: rawField, alias }
    })
    .filter(Boolean) as Array<{ field: string; alias: string }>
}

function attachInteractionCubeSlicers(
  typedSpec: TypedWorksheetSpec | null | undefined,
  queryOptions: Record<string, unknown>,
): void {
  if (!typedSpec || !Array.isArray((queryOptions as any)?.interactionCubeSlicers)) return
  const slicers = coerceInteractionCubeSlicersForTypedSpec(
    (queryOptions as any).interactionCubeSlicers,
    typedSpec,
  )
  if (slicers.length > 0) {
    ;(typedSpec as any).interactionCubeSlicers = slicers
  }
}

function mergeQueryOptions(
  base: WorksheetQueryOptions | Record<string, unknown> | null | undefined,
  overrides: WorksheetCompilerInputOptions['queryOptionsOverrides'],
): WorksheetQueryOptions {
  const baseSafe = (base ?? {}) as Record<string, unknown>
  const overridesSafe = (overrides ?? {}) as Record<string, unknown>
  const merged = { ...baseSafe, ...overridesSafe } as WorksheetQueryOptions
  if (!merged.sourceSchema && (baseSafe as any).sourceSchema) merged.sourceSchema = (baseSafe as any).sourceSchema
  if (!merged.sourceSchema && (overridesSafe as any).source_schema) merged.sourceSchema = (overridesSafe as any).source_schema
  if (merged.sourceSchema && !Array.isArray(merged.extractColumns)) {
    merged.extractColumns = merged.sourceSchema.columns.map((column) => column.name)
  }
  return merged
}

/**
 * Workstream A step 7 diagnostic hook — reports every live-path compile
 * whose authoring-to-typed lowering rejected and therefore cannot compile
 * under the Rust-only gate. Intentionally deduped by
 * `callerTag::worksheetName::reason` so a single reason on a single
 * worksheet shows up once per boot rather than once per render.
 *
 * This is now a stop condition in production: the adapter runs in
 * `typed-required` mode after Rust install, so any rejection hard fails
 * instead of routing through TS legacy.
 *
 * Silent in tests (when `globalThis.process` is available and
 * `NODE_ENV === 'test'`) so the conformance harness' own
 * `[typed-diag]` logging remains the authoritative per-run surface.
 */
const liveTypedSpecRejectionLogCache = new Set<string>()

function logLiveTypedSpecRejection(
  callerTag: string,
  worksheetName: string | null | undefined,
  reason: string | null | undefined,
): void {
  if (!reason) return
  try {
    const proc =
      typeof globalThis !== 'undefined'
        ? ((globalThis as any).process as { env?: Record<string, string | undefined> } | undefined)
        : undefined
    if (proc?.env?.NODE_ENV === 'test' && proc?.env?.FG_LIVE_TYPED_REJECT_LOG !== '1') return
  } catch {}
  const wsName = typeof worksheetName === 'string' && worksheetName ? worksheetName : '<unnamed>'
  const dedupKey = `${callerTag}::${wsName}::${reason}`
  if (liveTypedSpecRejectionLogCache.has(dedupKey)) return
  liveTypedSpecRejectionLogCache.add(dedupKey)
  try {
    console.warn(
      `[fg-compiler] live typed-spec lowering rejected: worksheet="${wsName}" callerTag=${callerTag} reason=${reason} — strict typed-required mode will hard fail this compile until authoring lowering covers this shape.`,
    )
  } catch {}
}

export function __resetLiveTypedSpecRejectionLogCacheForTest(): void {
  liveTypedSpecRejectionLogCache.clear()
}

function resolveWorksheetSpecNameForTypedInput(
  worksheetSpec: WorksheetSpecV1 | null | undefined,
  fallback?: string | null,
): string | null {
  const candidates = [
    fallback,
    (worksheetSpec as any)?.name,
    (worksheetSpec as any)?.worksheet_name,
    (worksheetSpec as any)?.source?.tableau?.worksheet_name,
  ]
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue
    const trimmed = candidate.trim()
    if (trimmed.length > 0) return trimmed
  }
  return null
}

/**
 * Builder for `buildLocalWorksheetExecutionCorePlan()` in
 * `worksheetExecutionCore.ts`.
 *
 * This is the richest live input path: it has the full prepared core, so
 * downstream (Rust in PR2+) can see the normalized request / policy /
 * semantic plan without back-deriving any of it from queryOptions.
 */
export function buildWorksheetCompilerInputFromPreparedCore(
  prepared: PreparedWorksheetExecutionCore,
  options: WorksheetCompilerInputOptions & {
    /**
     * Caller tag. Both the legacy and semantic sides of the drift check
     * MUST pass the same input shape but may differ only in this tag;
     * the factory preserves that so diagnostics still show which side
     * surfaced a fault.
     */
    callerTag: Extract<
      LiveCompileCallerTag,
      'buildLocalWorksheetExecutionCorePlan' | 'buildLocalWorksheetExecutionCorePlan.semantic'
    >
  },
): CompileWorksheetExecutionInput {
  const worksheetSpec = prepared.request.normalizedRequest.worksheetSpec as WorksheetSpecV1
  const mergedQueryOptions = mergeQueryOptions(prepared.queryOptions, options.queryOptionsOverrides)
  const resolvedActionState = resolveActionStateForInput(worksheetSpec, options)
  const runtimeFilters = resolveRuntimeFiltersForTypedInput(
    resolvedActionState,
    (mergedQueryOptions as any)?.filters,
  )
  // Workstream A step 6: attempt authoring-to-typed lowering. A null
  // typedSpec is now a hard compile failure under production
  // `typed-required` strictness, with `reason` carrying the root-cause
  // diagnostic tag for the lowering gap.
  const loweringOutcome = tryBuildTypedSpecFromInputs({
    worksheetSpec,
    runtimeFilters,
    measureValuesOverride:
      (mergedQueryOptions as any)?.measureValuesOverride ??
      prepared.request.normalizedRequest.measureValuesOverride,
    worksheetName: resolveWorksheetSpecNameForTypedInput(
      worksheetSpec,
      prepared.request.normalizedRequest.worksheetName,
    ),
    workbookIr:
      (mergedQueryOptions as any)?.workbookIr ??
      (prepared.request as any).authoringState?.workbookIr ??
      null,
    fieldManifest:
      (mergedQueryOptions as any)?.fieldManifest ??
      (mergedQueryOptions as any)?.field_manifest ??
      null,
    semanticSpec:
      (mergedQueryOptions as any)?.semanticSpec ??
      (mergedQueryOptions as any)?.semantic_spec ??
      null,
    extractUri: (mergedQueryOptions as any)?.extractUri,
    baseFromSql:
      (mergedQueryOptions as any)?.baseFromSql ??
      (mergedQueryOptions as any)?.base_from_sql ??
      null,
    limit: (mergedQueryOptions as any)?.limit,
    pagingProjection: (mergedQueryOptions as any)?.pagingProjection ?? null,
  })
  attachInteractionCubeSlicers(loweringOutcome.typedSpec, mergedQueryOptions as any)
  const requestParameters = prepared.request.normalizedRequest.parameters
  // When the lowering produced a typedSpec we route to Rust, which
  // requires (a) serde-tagged TypedCalcLiteral shape and (b) every bound
  // parameter to match a declared `typedSpec.parameters` entry. If the
  // typed spec declares no parameters, drop all bindings so Rust does
  // not reject on unknown names — the typed spec by construction cannot
  // reference parameters it doesn't declare. When lowering rejects
  // (no typedSpec), production strictness fails before these bindings
  // can reach a backend; tests may still inspect the raw input shape.
  const haveTypedSpec = loweringOutcome.typedSpec != null
  const declaredParamTypes = new Map(
    (loweringOutcome.typedSpec?.parameters ?? []).map((p) => [p.name, p.ty] as const),
  )
  const boundParameters =
    requestParameters && Object.keys(requestParameters).length > 0
      ? haveTypedSpec
        ? coerceBoundParametersForRust(requestParameters, declaredParamTypes)
        : { ...requestParameters }
      : null
  logLiveTypedSpecRejection(
    options.callerTag,
    resolveWorksheetSpecNameForTypedInput(
      worksheetSpec,
      prepared.request.normalizedRequest.worksheetName,
    ),
    loweringOutcome.reason,
  )
  maybeLogFilterTypedLowering({
    callerTag: options.callerTag,
    worksheetName: prepared.request.normalizedRequest.worksheetName,
    queryOptions: mergedQueryOptions,
    runtimeFilters,
    typedSpec: loweringOutcome.typedSpec,
    rejectionReason: loweringOutcome.reason,
  })
  return {
    worksheetSpec,
    queryOptions: mergedQueryOptions,
    emitExecPlan: Boolean(options.emitExecPlan),
    preparedExecutionCore: prepared,
    typedSpec: loweringOutcome.typedSpec,
    typedSpecRejectionReason: loweringOutcome.reason,
    boundParameters,
    resolvedActionState,
    callerTag: options.callerTag,
    pagingProjection: (mergedQueryOptions as any)?.pagingProjection ?? null,
  }
}

/**
 * Builder for `renderVizqlPlanExecution()` in `vizqlPlan/index.ts`.
 *
 * This path does not carry a `PreparedWorksheetExecutionCore`; callers
 * that have one should use `buildWorksheetCompilerInputFromPreparedCore`
 * instead so Rust (PR2+) sees the richer live context.
 */
export function buildWorksheetCompilerInputFromVizqlPlan(
  plan: VizqlPlan | SemanticWorksheetPlan,
  options: WorksheetCompilerInputOptions & {
    callerTag: Extract<LiveCompileCallerTag, 'renderVizqlPlanExecution'>
  },
): CompileWorksheetExecutionInput {
  const worksheetSpec = plan.worksheetSpec as WorksheetSpecV1
  const worksheetName = resolveWorksheetSpecNameForTypedInput(worksheetSpec)
  const mergedOptions = mergeQueryOptions((plan.options || {}) as any, options.queryOptionsOverrides)
  const resolvedActionState = resolveActionStateForInput(worksheetSpec, options)
  const runtimeFilters = resolveRuntimeFiltersForTypedInput(resolvedActionState, (mergedOptions as any)?.filters)
  const lowering = tryBuildTypedSpecFromInputs({
    worksheetSpec,
    runtimeFilters,
    measureValuesOverride: (mergedOptions as any)?.measureValuesOverride ?? null,
    worksheetName,
    workbookIr: (mergedOptions as any)?.workbookIr ?? null,
    fieldManifest:
      (mergedOptions as any)?.fieldManifest ??
      (mergedOptions as any)?.field_manifest ??
      null,
    semanticSpec: (mergedOptions as any)?.semanticSpec ?? (mergedOptions as any)?.semantic_spec ?? null,
    extractUri: (mergedOptions as any)?.extractUri,
    baseFromSql:
      (mergedOptions as any)?.baseFromSql ??
      (mergedOptions as any)?.base_from_sql ??
      null,
    limit: (mergedOptions as any)?.limit,
    pagingProjection: (mergedOptions as any)?.pagingProjection ?? null,
  })
  attachInteractionCubeSlicers(lowering.typedSpec, mergedOptions as any)
  logLiveTypedSpecRejection(
    options.callerTag,
    worksheetName,
    lowering.reason,
  )
  maybeLogFilterTypedLowering({
    callerTag: options.callerTag,
    worksheetName,
    queryOptions: mergedOptions,
    runtimeFilters,
    typedSpec: lowering.typedSpec,
    rejectionReason: lowering.reason,
  })
  return {
    worksheetSpec,
    queryOptions: mergedOptions,
    emitExecPlan: Boolean(options.emitExecPlan),
    resolvedActionState,
    typedSpec: lowering.typedSpec,
    typedSpecRejectionReason: lowering.reason,
    callerTag: options.callerTag,
    pagingProjection: (mergedOptions as any)?.pagingProjection ?? null,
  }
}

/**
 * Builder for the worker `run_query_tab` path in `src/worker/worker.ts`.
 *
 * The worker has a `VizqlPlan` plus a pair of flags (`emitExecPlan`,
 * `shouldEmitCompiledExecPlan` which maps to `collectExecPlan`). It does
 * NOT have a full `PreparedWorksheetExecutionCore`; the worker operates
 * on the serialized vizql plan that arrived from the main-thread
 * compiler.
 *
 * Keeping the worker-side factory distinct from the main-thread vizql
 * factory lets Rust/WASM see the worker provenance explicitly, which the
 * plan calls out as important for post-switch telemetry.
 */
export function buildWorksheetCompilerInputFromWorker(args: {
  vizqlPlan: VizqlPlan
  emitExecPlan: boolean
  shouldEmitCompiledExecPlan: boolean
  /**
   * PR3 — optional pre-resolved dashboard action state from the main
   * thread. The worker never has a raw `ActionResult`; the main
   * thread already resolved it and passes the normalized shape
   * through the vizql plan payload.
   */
  resolvedActionState?: ResolvedWorksheetActionState | null
}): CompileWorksheetExecutionInput {
  const worksheetSpec = args.vizqlPlan.worksheetSpec as WorksheetSpecV1
  const worksheetName = resolveWorksheetSpecNameForTypedInput(worksheetSpec)
  const mergedOptions = mergeQueryOptions(args.vizqlPlan.options, {
    emitExecPlan: args.emitExecPlan,
    collectExecPlan: args.shouldEmitCompiledExecPlan,
  })
  const resolvedActionState = args.resolvedActionState ?? null
  const runtimeFilters = resolveRuntimeFiltersForTypedInput(resolvedActionState, (mergedOptions as any)?.filters)
  const lowering = tryBuildTypedSpecFromInputs({
    worksheetSpec,
    runtimeFilters,
    measureValuesOverride: (mergedOptions as any)?.measureValuesOverride ?? null,
    worksheetName,
    workbookIr: (mergedOptions as any)?.workbookIr ?? null,
    fieldManifest:
      (mergedOptions as any)?.fieldManifest ??
      (mergedOptions as any)?.field_manifest ??
      null,
    semanticSpec: (mergedOptions as any)?.semanticSpec ?? (mergedOptions as any)?.semantic_spec ?? null,
    extractUri: (mergedOptions as any)?.extractUri,
    baseFromSql:
      (mergedOptions as any)?.baseFromSql ??
      (mergedOptions as any)?.base_from_sql ??
      null,
    limit: (mergedOptions as any)?.limit,
    pagingProjection: (mergedOptions as any)?.pagingProjection ?? null,
  })
  attachInteractionCubeSlicers(lowering.typedSpec, mergedOptions as any)
  logLiveTypedSpecRejection(
    'worker.run_query_tab',
    worksheetName,
    lowering.reason,
  )
  maybeLogFilterTypedLowering({
    callerTag: 'worker.run_query_tab',
    worksheetName,
    queryOptions: mergedOptions,
    runtimeFilters,
    typedSpec: lowering.typedSpec,
    rejectionReason: lowering.reason,
  })
  return {
    worksheetSpec,
    queryOptions: mergedOptions,
    emitExecPlan: args.emitExecPlan,
    resolvedActionState,
    typedSpec: lowering.typedSpec,
    typedSpecRejectionReason: lowering.reason,
    pagingProjection: (mergedOptions as any)?.pagingProjection ?? null,
    callerTag: 'worker.run_query_tab',
  }
}

/**
 * Dev-time assertion helper for the cutover PR (PR6). Until the Rust
 * backend is default-on, this stays unused at runtime but is imported
 * and exercised by tests so the invariant survives future refactors.
 *
 * Graduated strictness matches
 * `worksheetCompilerAdapter.setWorksheetCompilerStrictSwitchMode`:
 *
 *   - `'caller-tag'` (default here): every input must carry a
 *     `callerTag` — no anonymous live paths.
 *   - `'typed-required'`: additionally every input must carry a
 *     non-null `typedSpec` produced by the authoring-to-typed lowering.
 *     This is the PR6 exit criterion: once the Rust backend is the
 *     only live backend, any input missing a lowered spec is a
 *     regression, and surfacing `typedSpecRejectionReason` in the
 *     error keeps the root-cause diagnostic inline.
 */
export type LiveCompileInputStrictness = 'caller-tag' | 'typed-required'

export function assertLiveCompileInputIsSwitchReady(
  input: CompileWorksheetExecutionInput,
  strictness: LiveCompileInputStrictness = 'caller-tag',
): void {
  if (!input.callerTag) {
    throw new Error(
      'compileWorksheetExecution input is missing `callerTag`; every live path must go through `buildWorksheetCompilerInput.ts`.',
    )
  }
  if (strictness === 'typed-required' && !input.typedSpec) {
    throw new Error(
      'compileWorksheetExecution input is missing `typedSpec` under typed-required strictness; ' +
        'authoring-to-typed lowering rejected with reason: ' +
        `${input.typedSpecRejectionReason ?? 'unknown'} (callerTag=${input.callerTag}).`,
    )
  }
}
